import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, Palette, Code, Video, Camera, PenTool, Edit3, Megaphone, Sparkles,
  Star, Briefcase, Euro, Clock, Calendar, CheckCircle, Lock, ExternalLink, 
  Phone, Mail, Linkedin, Play, FileText, Send, Loader2, TrendingUp, Award
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import SignatureCanvas from 'react-signature-canvas';

// Using api from lib/axios

const SPECIALTIES = [
  { id: 'graphic-design', label: 'Design Graphique', icon: Palette, color: 'from-pink-500 to-rose-600' },
  { id: 'web-development', label: 'Développement Web', icon: Code, color: 'from-cyan-500 to-blue-600' },
  { id: 'video-production', label: 'Production Vidéo', icon: Video, color: 'from-purple-500 to-violet-600' },
  { id: 'motion-design', label: 'Motion Design', icon: Sparkles, color: 'from-amber-500 to-orange-600' },
  { id: 'photography', label: 'Photographie', icon: Camera, color: 'from-emerald-500 to-green-600' },
  { id: 'copywriting', label: 'Rédaction', icon: Edit3, color: 'from-blue-500 to-indigo-600' },
  { id: 'social-media', label: 'Social Media', icon: Megaphone, color: 'from-red-500 to-pink-600' },
  { id: 'illustration', label: 'Illustration', icon: PenTool, color: 'from-teal-500 to-cyan-600' },
];

const CreativeProfilePage = () => {
  const { creativeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [creative, setCreative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  
  // Mission Modal
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [missionForm, setMissionForm] = useState({
    mission_type: 'ponctuelle',
    budget_type: 'hourly',
    hourly_rate: '',
    fixed_amount: '',
    brief: '',
    deadline: '',
    estimated_hours: '',
    project_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Upgrade Modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeRate, setUpgradeRate] = useState('');

  useEffect(() => {
    fetchCreative();
    fetchProjects();
  }, [creativeId]);

  const fetchCreative = async () => {
    setLoading(true);
    try {
      const response = await api.get('/creatives/${creativeId}');
      setCreative(response.data);
    } catch (error) {
      console.error('Error fetching creative:', error);
      toast.error("Erreur lors du chargement du profil");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const getSpecialtyConfig = (id) => {
    return SPECIALTIES.find(s => s.id === id) || { label: id, icon: Palette, color: 'from-slate-500 to-slate-600' };
  };

  const handleSubmitMission = async () => {
    if (!missionForm.brief) {
      toast.error("Veuillez remplir le brief de la mission");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        creative_id: creative.user_id,
        project_id: missionForm.project_id === 'none' ? null : missionForm.project_id || null,
        mission_type: missionForm.mission_type,
        budget_type: missionForm.budget_type,
        hourly_rate: missionForm.budget_type === 'hourly' ? parseFloat(missionForm.hourly_rate) || 0 : null,
        fixed_amount: missionForm.budget_type === 'fixed' ? parseFloat(missionForm.fixed_amount) || 0 : null,
        brief: missionForm.brief,
        deadline: missionForm.deadline ? new Date(missionForm.deadline).toISOString() : null,
        estimated_hours: parseFloat(missionForm.estimated_hours) || null
      };
      
      await axios.post(`${API}/missions/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Mission proposée avec succès !");
      setShowMissionModal(false);
      setMissionForm({
        mission_type: 'ponctuelle',
        budget_type: 'hourly',
        hourly_rate: '',
        fixed_amount: '',
        brief: '',
        deadline: '',
        estimated_hours: '',
        project_id: ''
      });
      fetchCreative();
    } catch (error) {
      console.error('Error creating mission:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création de la mission");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpgradeToIntegrated = async () => {
    if (!upgradeRate || parseFloat(upgradeRate) <= 0) {
      toast.error("Veuillez spécifier un taux horaire valide");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/missions/creative/${creative.user_id}/upgrade-to-integrated`,
        { hourly_rate: parseFloat(upgradeRate) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Créatif passé en mode Intégré !");
      setShowUpgradeModal(false);
      fetchCreative();
    } catch (error) {
      console.error('Error upgrading creative:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à niveau");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </MainLayout>
    );
  }

  if (!creative) {
    return (
      <MainLayout>
        <div className="text-center py-20">
          <h2 className="text-xl font-bold text-slate-800 mb-2">Créatif non trouvé</h2>
          <Button onClick={() => navigate('/annuaire-creatifs')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l&apos;annuaire
          </Button>
        </div>
      </MainLayout>
    );
  }

  const userInfo = creative.user || {};
  const progression = creative.progression || {};

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate('/annuaire-creatifs')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l&apos;annuaire
        </Button>

        {/* Profile Header */}
        <Card className="overflow-hidden">
          <div className={cn(
            "h-32 md:h-40 relative bg-gradient-to-br",
            creative.specialties?.[0] ? getSpecialtyConfig(creative.specialties[0]).color : "from-slate-400 to-slate-500"
          )}>
            {/* Status badges */}
            <div className="absolute top-4 right-4 flex gap-2">
              {creative.creative_type === 'integre' ? (
                <Badge className="bg-emerald-500 text-white">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Créatif Intégré
                </Badge>
              ) : creative.creative_status === 'active' ? (
                <Badge className="bg-amber-500 text-white">
                  En mission
                </Badge>
              ) : (
                <Badge className="bg-slate-500 text-white">
                  <Lock className="w-3 h-3 mr-1" />
                  Disponible
                </Badge>
              )}
            </div>
          </div>
          
          <CardContent className="relative pt-0">
            {/* Avatar */}
            <div className="relative -mt-16 md:-mt-20 mb-4 flex flex-col md:flex-row md:items-end gap-4">
              <Avatar className="w-24 h-24 md:w-32 md:h-32 border-4 border-white shadow-lg">
                <AvatarImage src={userInfo.avatar_url} alt={userInfo.first_name} />
                <AvatarFallback className="bg-slate-200 text-slate-600 text-2xl md:text-3xl font-bold">
                  {userInfo.first_name?.[0]}{userInfo.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                  {userInfo.first_name} {userInfo.last_name}
                </h1>
                
                {/* Specialties */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {creative.specialties?.map((spec, i) => {
                    const config = getSpecialtyConfig(spec);
                    const Icon = config.icon;
                    return (
                      <Badge key={i} variant="outline" className="gap-1">
                        <Icon className="w-3 h-3" />
                        {config.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
              
              {/* Action Buttons */}
              {user?.role === 'agency' && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    onClick={() => setShowMissionModal(true)}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Proposer une mission
                  </Button>
                  
                  {progression.can_become_integrated && creative.creative_type !== 'integre' && (
                    <Button 
                      variant="outline"
                      onClick={() => setShowUpgradeModal(true)}
                      className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Passer en Intégré
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-t border-b">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-amber-500">
                  <Star className="w-5 h-5 fill-amber-400" />
                  <span className="text-2xl font-bold text-slate-800">{creative.average_rating || 0}</span>
                  <span className="text-slate-500">/20</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Note moyenne</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-800">{creative.projects_completed || 0}</div>
                <p className="text-xs text-slate-500 mt-1">Projets livrés</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-800">{creative.years_experience || 0}</div>
                <p className="text-xs text-slate-500 mt-1">Ans d&apos;expérience</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-800">
                  {creative.integrated_hourly_rate || userInfo.hourly_rate || '-'}€
                </div>
                <p className="text-xs text-slate-500 mt-1">Taux horaire</p>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="flex flex-wrap gap-4 py-4">
              {userInfo.email && (
                <a href={`mailto:${userInfo.email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600">
                  <Mail className="w-4 h-4" />
                  {userInfo.email}
                </a>
              )}
              {userInfo.phone && (
                <a href={`tel:${userInfo.phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600">
                  <Phone className="w-4 h-4" />
                  {userInfo.phone}
                </a>
              )}
              {creative.linkedin_url && (
                <a href={creative.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600">
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </a>
              )}
              {creative.portfolio_url && (
                <a href={creative.portfolio_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600">
                  <ExternalLink className="w-4 h-4" />
                  Portfolio
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="about" className="space-y-4">
          <TabsList>
            <TabsTrigger value="about">À propos</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="missions">Missions</TabsTrigger>
            <TabsTrigger value="ratings">Évaluations</TabsTrigger>
            {user?.role === 'agency' && (
              <TabsTrigger value="progression">Progression</TabsTrigger>
            )}
          </TabsList>

          {/* About Tab */}
          <TabsContent value="about">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Biographie</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 whitespace-pre-wrap">
                    {creative.bio || "Aucune biographie renseignée."}
                  </p>
                </CardContent>
              </Card>
              
              {creative.presentation_video_url && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      Vidéo de présentation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                      <iframe 
                        src={creative.presentation_video_url}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {creative.portfolio_items?.length > 0 ? (
                creative.portfolio_items.map((item, i) => (
                  <Card key={i} className="overflow-hidden group">
                    {item.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={item.image_url} 
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <h4 className="font-medium text-slate-800">{item.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full py-12">
                  <div className="text-center text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucun élément de portfolio</p>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Missions Tab */}
          <TabsContent value="missions">
            <div className="space-y-4">
              {creative.missions?.length > 0 ? (
                creative.missions.map((mission) => (
                  <Card key={mission.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={mission.mission_type === 'reguliere' ? 'default' : 'outline'}>
                              {mission.mission_type === 'reguliere' ? 'Régulière' : 'Ponctuelle'}
                            </Badge>
                            <Badge variant="outline" className={cn(
                              mission.status === 'completed' && 'bg-emerald-50 text-emerald-700',
                              mission.status === 'active' && 'bg-blue-50 text-blue-700',
                              mission.status === 'signed' && 'bg-amber-50 text-amber-700',
                              mission.status === 'proposed' && 'bg-slate-50 text-slate-700'
                            )}>
                              {mission.status === 'completed' && 'Terminée'}
                              {mission.status === 'active' && 'Active'}
                              {mission.status === 'signed' && 'Signée'}
                              {mission.status === 'proposed' && 'Proposée'}
                            </Badge>
                          </div>
                          <p className="text-slate-600 mt-2">{mission.brief}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            {mission.hourly_rate && (
                              <span className="flex items-center gap-1">
                                <Euro className="w-3 h-3" />
                                {mission.hourly_rate}€/h
                              </span>
                            )}
                            {mission.fixed_amount && (
                              <span className="flex items-center gap-1">
                                <Euro className="w-3 h-3" />
                                {mission.fixed_amount}€ (fixe)
                              </span>
                            )}
                            {mission.deadline && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(mission.deadline).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                        </div>
                        {mission.project_name && (
                          <Badge variant="outline" className="flex-shrink-0">
                            {mission.project_name}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="py-12">
                  <div className="text-center text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucune mission</p>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings">
            <div className="space-y-4">
              {creative.ratings?.length > 0 ? (
                creative.ratings.map((rating) => (
                  <Card key={rating.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 text-amber-500">
                              <Star className="w-4 h-4 fill-amber-400" />
                              <span className="font-bold">{rating.rating}/20</span>
                            </div>
                            {rating.project_name && (
                              <span className="text-sm text-slate-500">
                                sur {rating.project_name}
                              </span>
                            )}
                          </div>
                          {rating.comment && (
                            <p className="text-slate-600 mt-2">&quot;{rating.comment}&quot;</p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(rating.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="py-12">
                  <div className="text-center text-slate-500">
                    <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucune évaluation</p>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Progression Tab (Agency only) */}
          {user?.role === 'agency' && (
            <TabsContent value="progression">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-cyan-500" />
                    Progression vers Créatif Intégré
                  </CardTitle>
                  <CardDescription>
                    Pour devenir un créatif intégré avec des missions régulières, ce créatif doit :
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Projects */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Projets livrés</span>
                      <span className="text-sm text-slate-500">
                        {progression.projects_delivered || 0} / 5
                      </span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={cn(
                          "h-full rounded-full transition-all",
                          (progression.projects_delivered || 0) >= 5 
                            ? "bg-emerald-500" 
                            : "bg-cyan-500"
                        )}
                        style={{ width: `${Math.min(100, ((progression.projects_delivered || 0) / 5) * 100)}%` }}
                      />
                    </div>
                    {progression.missing_projects > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Encore {progression.missing_projects} projet(s) à livrer
                      </p>
                    )}
                  </div>
                  
                  {/* Rating */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Note moyenne clients</span>
                      <span className="text-sm text-slate-500">
                        {progression.average_rating || 0} / 17 minimum
                      </span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={cn(
                          "h-full rounded-full transition-all",
                          (progression.average_rating || 0) >= 17 
                            ? "bg-emerald-500" 
                            : "bg-amber-500"
                        )}
                        style={{ width: `${Math.min(100, ((progression.average_rating || 0) / 20) * 100)}%` }}
                      />
                    </div>
                    {progression.missing_rating_points > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Encore {progression.missing_rating_points} point(s) nécessaire(s)
                      </p>
                    )}
                  </div>
                  
                  {/* Agency Approval */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Validation agence</p>
                      <p className="text-sm text-slate-500">Approbation finale pour le passage en Intégré</p>
                    </div>
                    {progression.can_become_integrated && creative.creative_type !== 'integre' ? (
                      <Button onClick={() => setShowUpgradeModal(true)} className="bg-emerald-500 hover:bg-emerald-600">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approuver
                      </Button>
                    ) : creative.creative_type === 'integre' ? (
                      <Badge className="bg-emerald-500">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Validé
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">
                        En attente des critères
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Mission Proposal Modal */}
      <Dialog open={showMissionModal} onOpenChange={setShowMissionModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proposer une mission</DialogTitle>
            <DialogDescription>
              Envoyez une proposition de mission à {userInfo.first_name} {userInfo.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Mission Type */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setMissionForm(prev => ({ ...prev, mission_type: 'ponctuelle' }))}
                className={cn(
                  "p-4 rounded-xl border-2 text-left transition-all",
                  missionForm.mission_type === 'ponctuelle'
                    ? "border-cyan-500 bg-cyan-50"
                    : "border-slate-200 hover:border-slate-300"
                )}
              >
                <Clock className="w-6 h-6 text-cyan-600 mb-2" />
                <h4 className="font-bold text-slate-800">Mission Ponctuelle</h4>
                <p className="text-xs text-slate-500 mt-1">
                  Pour un projet spécifique, accès temporaire
                </p>
              </button>
              
              <button
                onClick={() => setMissionForm(prev => ({ ...prev, mission_type: 'reguliere' }))}
                className={cn(
                  "p-4 rounded-xl border-2 text-left transition-all",
                  missionForm.mission_type === 'reguliere'
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:border-slate-300"
                )}
              >
                <CheckCircle className="w-6 h-6 text-emerald-600 mb-2" />
                <h4 className="font-bold text-slate-800">Mission Régulière</h4>
                <p className="text-xs text-slate-500 mt-1">
                  Collaboration continue, accès permanent
                </p>
              </button>
            </div>
            
            {/* Project Selection */}
            <div>
              <Label>Projet associé (optionnel)</Label>
              <Select 
                value={missionForm.project_id} 
                onValueChange={(v) => setMissionForm(prev => ({ ...prev, project_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner un projet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun projet</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Brief */}
            <div>
              <Label>Brief de la mission *</Label>
              <Textarea 
                value={missionForm.brief}
                onChange={(e) => setMissionForm(prev => ({ ...prev, brief: e.target.value }))}
                placeholder="Décrivez la mission, les livrables attendus..."
                className="mt-1 min-h-[120px]"
              />
            </div>
            
            {/* Budget */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de budget</Label>
                <Select 
                  value={missionForm.budget_type} 
                  onValueChange={(v) => setMissionForm(prev => ({ ...prev, budget_type: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Taux horaire</SelectItem>
                    <SelectItem value="fixed">Budget fixe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {missionForm.budget_type === 'hourly' ? (
                <div>
                  <Label>Taux horaire (€/h)</Label>
                  <Input 
                    type="number"
                    value={missionForm.hourly_rate}
                    onChange={(e) => setMissionForm(prev => ({ ...prev, hourly_rate: e.target.value }))}
                    placeholder="45"
                    className="mt-1"
                  />
                </div>
              ) : (
                <div>
                  <Label>Montant fixe (€)</Label>
                  <Input 
                    type="number"
                    value={missionForm.fixed_amount}
                    onChange={(e) => setMissionForm(prev => ({ ...prev, fixed_amount: e.target.value }))}
                    placeholder="1500"
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            
            {/* Deadline & Hours */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date limite</Label>
                <Input 
                  type="date"
                  value={missionForm.deadline}
                  onChange={(e) => setMissionForm(prev => ({ ...prev, deadline: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Heures estimées</Label>
                <Input 
                  type="number"
                  value={missionForm.estimated_hours}
                  onChange={(e) => setMissionForm(prev => ({ ...prev, estimated_hours: e.target.value }))}
                  placeholder="20"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowMissionModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmitMission} 
              disabled={submitting || !missionForm.brief}
              className="bg-gradient-to-r from-cyan-500 to-blue-600"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Envoyer la proposition
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade to Integrated Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-emerald-500" />
              Passer en Créatif Intégré
            </DialogTitle>
            <DialogDescription>
              {userInfo.first_name} {userInfo.last_name} remplit les conditions pour devenir un créatif intégré.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-emerald-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="w-4 h-4" />
                <span>{progression.projects_delivered || 0} projets livrés</span>
              </div>
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="w-4 h-4" />
                <span>Note moyenne: {progression.average_rating || 0}/20</span>
              </div>
            </div>
            
            <div>
              <Label>Taux horaire contractuel (€/h) *</Label>
              <Input 
                type="number"
                value={upgradeRate}
                onChange={(e) => setUpgradeRate(e.target.value)}
                placeholder="55"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Ce taux sera appliqué pour toutes les futures missions régulières.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpgradeToIntegrated} 
              disabled={submitting || !upgradeRate}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmer le passage en Intégré
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default CreativeProfilePage;
